import { useEffect, useState, type ReactNode } from 'react';

/** Still first-frame <img> until hover/focus, then animated `children`. */
export function ClientSideHoverFreeze({
  children,
  src,
  className,
}: {
  children: ReactNode;
  src: string;
  className?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [freezeSrc, setFreezeSrc] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | undefined;
    setFreezeSrc(null);

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = src;

    const publish = (nextSrc: string, isObjectUrl: boolean) => {
      if (!isMounted) {
        if (isObjectUrl) URL.revokeObjectURL(nextSrc);
        return;
      }
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = isObjectUrl ? nextSrc : undefined;
      setFreezeSrc(nextSrc);
    };

    const handleLoad = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (w <= 0 || h <= 0) return;

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      try {
        ctx.drawImage(img, 0, 0);
      } catch {
        publish(src, false);
        return;
      }

      canvas.toBlob((blob) => {
        if (blob) {
          publish(URL.createObjectURL(blob), true);
          return;
        }
        try {
          publish(canvas.toDataURL('image/png'), false);
        } catch {
          publish(src, false);
        }
      }, 'image/png');
    };

    img.addEventListener('load', handleLoad);
    if (img.complete && img.naturalWidth > 0) handleLoad();

    return () => {
      isMounted = false;
      img.removeEventListener('load', handleLoad);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        cursor: 'pointer',
        verticalAlign: 'middle',
        borderRadius: 'inherit',
      }}
    >
      <div
        style={{
          opacity: isHovered || !freezeSrc ? 1 : 0,
          display: 'flex',
          width: '100%',
        }}
      >
        {children}
      </div>
      {freezeSrc ? (
        <img
          src={freezeSrc}
          alt=""
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'inherit',
            opacity: !isHovered ? 1 : 0,
            pointerEvents: 'none',
            display: 'block',
          }}
        />
      ) : null}
    </div>
  );
}
